import React, { useCallback, useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';

export default function UploadArea({ onFile, onFiles, onAppendFiles, selectedFile, selectedFiles, onRemoveFile, onReorderFiles, deviceNames = [], isGlobalDragging = false }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Combine local and global dragging states
  const showDragState = isDragging || isGlobalDragging;
  const [hoveredThumbnail, setHoveredThumbnail] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [imageLoading, setImageLoading] = useState({});

  // Set loading state only when new files are being processed
  useEffect(() => {
    // Only show loading state for newly added files, not existing ones
    if (selectedFiles && selectedFiles.length > 1) {
      const loadingState = {};
      selectedFiles.forEach((_, index) => {
        // Only show loading for the last few files that were just added
        // This is a simplified approach - in a real app you'd track which files are new
        loadingState[index] = false; // Don't show loading for existing thumbnails
      });
      setImageLoading(loadingState);
    } else if (selectedFiles && selectedFiles.length === 1) {
      setImageLoading({ 'single': false });
    } else if (selectedFile) {
      setImageLoading({ 'single-file': false });
    }
  }, [selectedFiles, selectedFile]);

  const handleFiles = useCallback((files) => {
    console.log('handleFiles called with:', files);
    const fileArray = Array.from(files).filter(file => file.type.startsWith('image/'));
    console.log('Filtered files:', fileArray.length, 'files');
    if (fileArray.length > 0) {
      // If there are already files selected, append new files
      if ((selectedFiles && selectedFiles.length > 0) || selectedFile) {
        console.log('Appending files to existing selection');
        onAppendFiles(fileArray);
      } else {
        // No existing files, handle normally
        if (fileArray.length === 1) {
          console.log('Single file selected:', fileArray[0].name);
          onFile(fileArray[0]);
        } else {
          console.log('Multiple files selected:', fileArray.length);
          onFiles(fileArray);
        }
      }
    } else {
      console.log('No valid image files found');
    }
    // Clear the input value to ensure onChange fires on subsequent selections
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [onFile, onFiles, onAppendFiles, selectedFile, selectedFiles]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    console.log('Files dropped:', e.dataTransfer.files);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback(() => setIsDragging(false), []);

  const handleRemoveFile = useCallback((index) => {
    if (onRemoveFile && selectedFiles) {
      const newFiles = selectedFiles.filter((_, i) => i !== index);
      if (newFiles.length === 0) {
        onFiles([]);
      } else if (newFiles.length === 1) {
        onFile(newFiles[0]);
      } else {
        onFiles(newFiles);
      }
    }
  }, [onRemoveFile, selectedFiles, onFiles, onFile]);

  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex && onReorderFiles) {
      const newFiles = [...selectedFiles];
      const draggedFile = newFiles[draggedIndex];
      newFiles.splice(draggedIndex, 1);
      newFiles.splice(dropIndex, 0, draggedFile);
      onReorderFiles(newFiles);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, selectedFiles, onReorderFiles]);

  return (
    <motion.div
      className={cn(
        'w-full p-6 text-center rounded-[24px] border-2 transition flex flex-col items-center justify-center bg-card overflow-hidden',
        showDragState ? 'border-foreground bg-muted/50' : 'border-muted',
        // Dynamic height based on content - can grow but not shrink below initial height
        selectedFile || (selectedFiles && selectedFiles.length > 0) 
          ? 'min-h-56' // Keep minimum height at initial height (h-56)
          : 'h-56' // Fixed height when empty
      )}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      animate={{ scale: 1 }}
    >
      {selectedFile || (selectedFiles && selectedFiles.length > 0) ? (
        <div className="mx-auto w-full max-w-full">
          {selectedFiles && selectedFiles.length > 1 ? (
            <div className="w-full">
              <div className={cn(
                "grid gap-2 sm:gap-3 mb-4 mx-auto p-2",
                selectedFiles.length === 2 
                  ? "grid-cols-2 max-w-64" 
                  : "grid-cols-2 sm:grid-cols-3 max-w-48"
              )}>
                {selectedFiles.slice(0, 9).map((file, index) => (
                  <div 
                    key={index} 
                    className={cn(
                      "relative group cursor-move transition-all duration-200",
                      draggedIndex === index && "opacity-50 scale-95",
                      dragOverIndex === index && "scale-105 ring-2 ring-foreground"
                    )}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onMouseEnter={() => setHoveredThumbnail(index)}
                    onMouseLeave={() => setHoveredThumbnail(null)}
                  >
                    {imageLoading[index] ? (
                      <div className="h-20 w-auto rounded-lg bg-surface-2 flex items-center justify-center">
                        <div className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="relative inline-block">
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={`Preview ${index + 1}`} 
                          className="h-20 w-auto rounded-lg object-contain bg-surface-1 shadow-sm upload-thumbnail pointer-events-none"
                          onLoad={() => setImageLoading(prev => ({ ...prev, [index]: false }))}
                          onError={() => setImageLoading(prev => ({ ...prev, [index]: false }))}
                        />
                        {hoveredThumbnail === index && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                            className="absolute top-0 right-0 w-5 h-5 bg-surface-1 rounded-full flex items-center justify-center hover:bg-surface-2 transition-colors shadow-sm z-20 transform translate-x-1 -translate-y-1"
                            title="Remove this image"
                          >
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="text-black dark:text-white"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {selectedFiles.length > 9 && (
                  <div className="h-20 w-auto rounded-lg bg-surface-2 flex items-center justify-center text-xs text-muted-foreground px-3">
                    +{selectedFiles.length - 9}
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-2">{selectedFiles.length} files selected</p>
              {deviceNames.length > 0 && (
                <div className="text-xs text-muted-foreground mb-3">
                  {deviceNames.join(', ')}
                </div>
              )}
            </div>
          ) : selectedFiles && selectedFiles.length === 1 ? (
            <div className="flex justify-center mb-4">
              <div className="relative">
                {imageLoading['single'] ? (
                  <div className="h-10 w-auto rounded-lg bg-surface-2 flex items-center justify-center">
                    <div className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="relative inline-block">
                    <img 
                      src={URL.createObjectURL(selectedFiles[0])} 
                      alt="Preview" 
                      className="h-20 w-auto rounded-lg object-contain bg-surface-1 shadow-sm upload-thumbnail pointer-events-none"
                      onLoad={() => setImageLoading(prev => ({ ...prev, 'single': false }))}
                      onError={() => setImageLoading(prev => ({ ...prev, 'single': false }))}
                    />
                    {hoveredThumbnail === 'single' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onFiles([]);
                        }}
                        className="absolute top-0 right-0 w-5 h-5 bg-surface-1 rounded-full flex items-center justify-center hover:bg-surface-2 transition-colors shadow-sm z-20 transform translate-x-1 -translate-y-1"
                        title="Remove this image"
                      >
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="text-black dark:text-white"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          
          
          {selectedFile && (!selectedFiles || selectedFiles.length === 0) && (
            <div className="flex justify-center mb-4">
              <div 
                className="relative"
                onMouseEnter={() => setHoveredThumbnail('single')}
                onMouseLeave={() => setHoveredThumbnail(null)}
              >
                {imageLoading['single-file'] ? (
                  <div className="h-10 w-auto rounded-lg bg-surface-2 flex items-center justify-center">
                    <div className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="relative inline-block">
                    <img 
                      src={URL.createObjectURL(selectedFile)} 
                      alt="Preview" 
                      className="h-20 w-auto rounded-lg object-contain bg-surface-1 shadow-sm upload-thumbnail pointer-events-none"
                      onLoad={() => setImageLoading(prev => ({ ...prev, 'single-file': false }))}
                      onError={() => setImageLoading(prev => ({ ...prev, 'single-file': false }))}
                    />
                    {hoveredThumbnail === 'single' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onFile(null);
                        }}
                        className="absolute top-0 right-0 w-5 h-5 bg-surface-1 rounded-full flex items-center justify-center hover:bg-surface-2 transition-colors shadow-sm z-20 transform translate-x-1 -translate-y-1"
                        title="Remove this image"
                      >
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="text-black dark:text-white"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => inputRef.current?.click()}
          >
            Add more files
          </button>
        </div>
      ) : (
        <div className="mx-auto space-y-2">
          <p className="text-foreground font-medium">Drag & drop screenshots</p>
          <p className="text-muted-foreground text-sm">or click to select files</p>
          <div className="pt-6">
            <button
              type="button"
              className="lickable-btn px-5 h-14 text-lg font-semibold"
              onClick={() => inputRef.current?.click()}
            >
              Choose Files
            </button>
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        name="screenshot"
        onChange={(e) => {
          console.log('File input changed:', e.target.files);
          handleFiles(e.target.files);
        }}
      />
    </motion.div>
  );
}



